import React, { Component, createContext, useContext } from 'react'
import { Auth0Context } from '@auth0/auth0-react'
import { connectorsService } from '../services/api/connectors.service'
import { settingsService } from '../services/api/settings.service'
import { datasetsService } from '../services/api/datasets.service'
import { isIframe } from '../services/utils'
import {accountService} from "../services/api/account.service";
import {LOCAL_APPS} from "../constants/Config";
import triggers_gpt from "../pages/auth/triggers_gpt";

// create the context
export const DataContext = createContext({})
export const useData = () => useContext(DataContext)

class DataProvider extends Component {
  static contextType = Auth0Context

  state = {
    isApiLoading: true,
    isIframeLogin: false,
    connectors: null,
    datasets: null,
    default_dataset: null,
    is_customer: null,
    init_status: null,
    payment_link: null,
    openai_connected: null,
    product: null,
    active_products: null,
    usage_limit: null,
    trial_usage: null,
    trial_status: null,
    bulk_licence: false,
    resolveloop_enabled: false,
    // E13 (MCP server only): {enabled, connection, connected, state} for the signed-in user,
    // decided by the API. null for every other product.
    mcp_user: null,
  }

  componentDidMount() {
    this.initData()
  }

  // initialize the auth0 library
  initData = async () => {
    this.setState({ isApiLoading: true })
    const auth0 = this.context
    let token = null
    try {
      token = await auth0.getAccessTokenSilently()
    } catch (err) {
      console.error('Got an error', err)
      if (isIframe()) {
        // return auth0.loginWithPopup()
        return this.setState({ isIframeLogin: true, isApiLoading: false })
      } else {
        this.setState({ isApiLoading: false })
        return auth0.loginWithRedirect()
      }
    }

    let product = this.props.product || localStorage.getItem('current_product')
    if (LOCAL_APPS.indexOf(product) === -1) {
      product = null;
    }
    else if (this.props.product) {
      localStorage.setItem('current_product', product)
    }
    let active_products
    try {
      active_products = await settingsService.getSetting(token, 'product')
      if (typeof active_products === 'string') {
        active_products = [active_products]
      }
      if (!product) {
        product = (active_products || []).find((p) => LOCAL_APPS.indexOf(p) >= 0)
        if (!product) {
          product = 'easydoc'
        }
      }
    } catch (err) {
      product = localStorage.getItem('swifteq_product')
      if (product) {
        try {
          await settingsService.updateSetting(token, 'product', [product])
        } catch (err) {
          console.error(err)
        }
      }
    }

    try {
      const [_connectors, account_info, datasets] = await Promise.all([
        connectorsService.getConnectors(token, product),
        accountService.getInfo(token, product),
        datasetsService.getDatasets(token),
      ])

      let default_dataset = datasets.find((d) => d.name === account_info.default_dataset || 'Zendesk Support')
      if (!default_dataset) {
        default_dataset = datasets[0]
      }
      if (default_dataset) {
        default_dataset.is_customer = account_info.is_customer;
      }

      const trial_info = {status: "done", tasks: {}}
      let usage_limit = {};
      if (account_info.product_account) {
        usage_limit = account_info.product_account.usage_limit || {};
        trial_info.status = account_info.product_account.trial_status || "done";
        trial_info.started_at = account_info.product_account.trial_started_at || undefined;
        if (trial_info.started_at) {
          const trial_length = (account_info.product_account.trial_length >= 0) ? account_info.product_account.trial_length : 14;
          const daysSinceStart = (new Date().getTime() - new Date(trial_info.started_at).getTime()) / (1000 * 3600 * 24);
          trial_info.days_to_expire = Math.ceil(trial_length - daysSinceStart);
        }
        Object.keys(account_info.product_account?.tasks || []).forEach((task_name) => {
          const task = account_info.product_account.tasks[task_name];
          trial_info.tasks[task_name] = {
            ...task,
            days_to_expire: (task.customer_until) ? Math.ceil((new Date(task.customer_until).getTime() - new Date().getTime()) / (1000 * 3600 * 24)) : undefined
          }
        })
      }

      const init_status = account_info.init_status === 'expired' || _connectors.some((c) => c.authenticated) ?
        account_info.init_status || 'registered' : 'registered';
      this.setState({
        connectors: (['redact', 'triggers_gpt', 'classify', 'lookup', 'document_parser', 'exportPdfs', 'mcpserver', 'resolveloop'].indexOf(product) >=0 || init_status !== 'expired') ? _connectors : null,
        datasets,
        default_dataset,
        is_customer: account_info.is_customer,
        init_status: init_status,
        payment_link: account_info.payment_link,
        openai_connected: account_info.openai_connected,
        product,
        active_products,
        isApiLoading: false,
        isIframeLogin: false,
        trial_usage: account_info.trial_usage || null,
        trial_info: trial_info,
        usage_limit: usage_limit,
        bulk_licence: account_info.bulk_licence,
        resolveloop_enabled: account_info.resolveloop_enabled || false,
        mcp_user: account_info.mcp_user || null,
      })
    } catch (err) {
      console.error('Got an error', err)
      this.setState({ isApiLoading: false })
    }
  }

  setOpenAIConnected = (val) => {
    this.setState({
      openai_connected: val,
    })
  }

  setDefaultDataset = (val) => {
    this.setState({
      default_dataset: val,
    })
  }

  setConnectors = (val) => {
    this.setState({
      connectors: val,
    })
  }

  setMcpUser = (val) => {
    this.setState({
      mcp_user: val,
    })
  }

  render() {
    const {
      isApiLoading,
      isIframeLogin,
      connectors,
      datasets,
      default_dataset,
      is_customer,
      init_status,
      payment_link,
      openai_connected,
      product,
      active_products,
      trial_usage,
      trial_info,
      usage_limit,
      bulk_licence,
      resolveloop_enabled,
      mcp_user,
    } = this.state
    const { children } = this.props

    const configObject = {
      isApiLoading,
      isIframeLogin,
      connectors,
      datasets,
      default_dataset,
      is_customer,
      init_status,
      payment_link,
      openai_connected,
      product,
      active_products,
      trial_usage,
      trial_info,
      usage_limit,
      bulk_licence,
      resolveloop_enabled,
      mcp_user,
      setOpenAIConnected: (connected) => this.setOpenAIConnected(connected),
      setDefaultDataset: (dataset) => this.setDefaultDataset(dataset),
      setConnectors: (connectors) => this.setConnectors(connectors),
      setMcpUser: (mcp_user) => this.setMcpUser(mcp_user),
      setTrialUsage: (trial_usage) => this.setState({ trial_usage }),
    }

    return <DataContext.Provider value={configObject}>{children}</DataContext.Provider>
  }
}

export default DataProvider
